import { useAppSelector } from "../../redux/redux";
import { useGetPermissionsQuery } from "../../slices/UserSlice";
import { canOpenReports } from "./reportsPermission";

/** Settings > Team permissions (same ids as Home and Billing use). */
export const VIEW_FIRM_WORK_PERMISSION = "4ef8ede7-80d5-43a4-9ad9-a9e08dbdb7c0";
export const VIEW_BILLING_PERMISSION = "434d4588-3532-4e3a-ac3c-d294ef2d76a6";
export { VIEW_REPORTS_PERMISSION } from "./reportsPermission";
/** canOpenReports for a user object (Admin = access level "Admin"). */
export const useCanOpenReports = (
  user:
    | {
        userRoleEnum?: { userRole?: string } | null;
        permissionIds?: string[] | null;
      }
    | null
    | undefined,
) => {
  const { data: permissions, isError } = useGetPermissionsQuery(undefined, {
    skip: !user,
  });
  if (!user) return undefined;
  return canOpenReports(
    user.userRoleEnum?.userRole === "Admin",
    user.permissionIds,
    permissions?.map((permission) => permission.id),
    isError,
  );
};

/**
 * Who sees what in Reports:
 * - the section itself: Admins and "View Reports" (`canOpen`, see above);
 * - work and time reports: the whole firm for Admins and "View firm's work",
 *   otherwise only the person's own work and time;
 * - billing reports (and money amounts on time): Admins and "View Billing".
 */
export const useReportsAccess = () => {
  const { user, isLoadingUser } = useAppSelector((state) => state.appReducer);
  const isAdmin = user?.userRoleEnum?.userRole === "Admin";
  const permissions: string[] = user?.permissionIds ?? [];
  const canOpen = useCanOpenReports(user?.id ? user : undefined);
  return {
    userId: user?.id as string | undefined,
    isAdmin,
    isLoading: isLoadingUser || !user?.id || canOpen === undefined,
    canOpen: !!canOpen,
    firmWide: isAdmin || permissions.includes(VIEW_FIRM_WORK_PERMISSION),
    billing: isAdmin || permissions.includes(VIEW_BILLING_PERMISSION),
  };
};

/** The team member filter to send: everyone's (as picked) or only mine. */
export const scopeUserIds = (
  access: ReturnType<typeof useReportsAccess>,
  picked: string[],
) => (access.firmWide ? picked : access.userId ? [access.userId] : []);
